/**
 * Markup de la galería `/reportes.html` — Fase 5f.
 *
 * Reusa las clases `.bgallery-*` de la galería de briefings para
 * heredar contenedor, tabla y estados. Añade las clases `.rgallery-*`
 * para las columnas específicas de reportes (teatro, duración,
 * pilotos, subido por, acciones) en `reportes-list.css`.
 *
 * Filas clicables: navegan a `/reportes/subir.html?id=<reportNumber>`
 * (modo edit del form de subida). El botón X intercepta el bubble
 * (`stopPropagation`) para que la fila no navegue al pulsarlo.
 */

import type { AuthenticatedPilot } from '@51esc/shared';
import { canDeleteMissionReport } from '../auth/permissions.js';
import type { MissionReportRow } from './api.js';

interface GalleryShellOptions {
  /** Si true, se pinta el botón "+ Subir reporte" (solo Oficial+). */
  canUpload: boolean;
}

interface EmptyRowOptions {
  canUpload: boolean;
}

export function renderGalleryShell({ canUpload }: GalleryShellOptions): string {
  const uploadBtn = canUpload
    ? `<a href="/reportes/subir.html" class="bgallery-new-btn">+ Subir reporte</a>`
    : '';
  return `
    <div class="bgallery-stage">
      <header class="bgallery-header">
        <h1 class="bgallery-title">REPORTES OFICIALES</h1>
        ${uploadBtn}
      </header>
      <div class="bgallery-table-wrap">
        <table class="bgallery-table">
          <thead>
            <tr>
              <th class="rgallery-col-num">Nº</th>
              <th class="rgallery-col-campaign">CAMPAÑA</th>
              <th class="rgallery-col-mission">MISIÓN</th>
              <th class="rgallery-col-theater">TEATRO</th>
              <th class="rgallery-col-date">FECHA</th>
              <th class="rgallery-col-duration">DURACIÓN</th>
              <th class="rgallery-col-pilots">PILOTOS</th>
              <th class="rgallery-col-uploader">SUBIDO POR</th>
              <th class="rgallery-col-uploaded">SUBIDO</th>
              <th class="rgallery-col-actions" aria-label="Acciones"></th>
            </tr>
          </thead>
          <tbody data-rgallery-rows>
            <tr><td colspan="10" class="bgallery-loading">cargando…</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

/**
 * Distintivo de estado para los reportes que el pipeline no terminó
 * (Fase 5h.3). Los `parsed` no llevan nada — son la mayoría y el
 * distintivo solo añadiría ruido.
 */
function renderStatusBadge(status: MissionReportRow['status']): string {
  if (status === 'parsed') return '';
  const isError = status === 'error';
  const label = isError ? 'CON ERROR' : 'PENDIENTE';
  const title = isError
    ? 'El pipeline no pudo procesarlo. Abre el reporte para reintentar o borrarlo.'
    : 'El pipeline aún no ha terminado con este reporte.';
  const variant = isError ? 'rgallery-badge--error' : 'rgallery-badge--pending';
  return ` <span class="rgallery-badge ${variant}" title="${escapeHtml(title)}">${label}</span>`;
}

export function renderRow(r: MissionReportRow, user: AuthenticatedPilot): string {
  const safeNumber = escapeHtml(r.reportNumber);
  const badge = renderStatusBadge(r.status);
  const campaign = escapeHtml(r.declaredCampaign?.trim() || '—');
  // MISIÓN prioriza lo que el oficial declaró a mano sobre el `title`
  // técnico del parser ("Caucasus_BFM_v3" → "Jericó 04"). Solo si no
  // declaró nada, fallback al title.
  const missionLabel = r.declaredMission?.trim() || r.title?.trim() || '—';
  const mission = escapeHtml(missionLabel);
  const theater = escapeHtml(r.theater?.trim() || '—');
  const date = escapeHtml(r.missionDateIso ?? '—');
  const duration = escapeHtml(formatDuration(r.durationSec));
  const pilots = String(r.pilotCount);
  const uploader = escapeHtml(r.uploadedByCallsign?.trim() || '—');
  const uploaded = escapeHtml(formatRelativeOrIso(r.uploadedAt));
  const deleteBtn = canDeleteMissionReport(user, r)
    ? `<button type="button" class="rgallery-delete-btn" data-rgallery-delete="${safeNumber}" title="Borrar reporte" aria-label="Borrar reporte ${safeNumber}">✕</button>`
    : '';

  return `
    <tr class="bgallery-row rgallery-row" data-rgallery-id="${safeNumber}" data-status="${r.status}" tabindex="0">
      <td class="rgallery-col-num">${safeNumber}${badge}</td>
      <td class="rgallery-col-campaign">${campaign}</td>
      <td class="rgallery-col-mission">${mission}</td>
      <td class="rgallery-col-theater">${theater}</td>
      <td class="rgallery-col-date">${date}</td>
      <td class="rgallery-col-duration bgallery-cell-num">${duration}</td>
      <td class="rgallery-col-pilots bgallery-cell-num">${pilots}</td>
      <td class="rgallery-col-uploader">${uploader}</td>
      <td class="rgallery-col-uploaded">${uploaded}</td>
      <td class="rgallery-col-actions">${deleteBtn}</td>
    </tr>
  `;
}

export function renderEmptyRow({ canUpload }: EmptyRowOptions): string {
  const cta = canUpload
    ? `<a href="/reportes/subir.html">Subir el primero →</a>`
    : 'Aún no hay reportes oficiales.';
  return `
    <tr>
      <td colspan="10" class="bgallery-empty">
        ${canUpload ? 'Aún no hay reportes oficiales. ' : ''}${cta}
      </td>
    </tr>
  `;
}

export function renderErrorRow(message: string): string {
  return `
    <tr>
      <td colspan="10" class="bgallery-error">${escapeHtml(message)}</td>
    </tr>
  `;
}

/**
 * Formatea segundos a `HH:MM` (siempre con horas, aunque sean 0). Si
 * no hay valor o es <60s, devuelve "—" — son casos de reporte muy
 * corto o metadata incompleta y mostrar "00:00" induce a error.
 */
function formatDuration(sec: number | null): string {
  if (sec === null || sec < 60) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${pad(h)}:${pad(m)}`;
}

/**
 * Formatea un ISO timestamp a algo legible. Para hoy: "hoy HH:MM";
 * para ayer: "ayer HH:MM"; más viejo: "YYYY-MM-DD".
 */
function formatRelativeOrIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `hoy ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return `ayer ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return d.toISOString().slice(0, 10);
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
